import { auth } from './auth'
import { analytics } from './analytics'

// Only report request timing to PostHog when it's actually slow — no point
// sending an event for every fast call. Adjust here, or just filter it out
// PostHog-side if you'd rather keep all of them and slice by duration there.
const SLOW_REQUEST_THRESHOLD_MS = 1000

const API_BASE = '/api'

type RequestOptions = {
  method?: string
  body?: unknown
}

// ── Silent token refresh ──────────────────────────────────────────────────────
// Singleton promise — deduplicates concurrent 401s so only one refresh call
// is in-flight at a time. All waiters share the same promise; the refresh
// endpoint re-sets the httpOnly access-token cookie itself (see
// gearlocker-api/src/routes/auth.js), so there is no token to read back here
// — a resolved promise just means "the cookie is fresh, go ahead and retry."
//
// Note: the various "Session expired" / "Not authenticated" throws below are
// NOT sent to analytics.captureException. Tokens expiring and redirecting to
// /login is routine, expected behavior, not a bug — capturing it would just
// add noise to error tracking every time a session naturally times out.
// Genuine request failures (non-401 errors) ARE captured, further down.

let _refreshPromise: Promise<void> | null = null

async function silentRefresh(): Promise<void> {
  const res = await fetch(`${API_BASE}/auth/refresh?app=web`, {
    method: 'POST',
    credentials: 'include', // sends the sealed session cookie
  })
  if (!res.ok) {
    auth.markLoggedOut()
    window.location.href = '/login'
    throw new Error('Session expired')
  }
  auth.markAuthenticated()
}

function attemptRefresh(): Promise<void> {
  if (!_refreshPromise) {
    _refreshPromise = silentRefresh().finally(() => { _refreshPromise = null })
  }
  return _refreshPromise
}

// ── fetchWithAuth ─────────────────────────────────────────────────────────────
// Wraps raw fetch with the same refresh-and-retry logic used by apiRequest.
// Use this for multipart/FormData requests that can't go through apiRequest.
// No Authorization header to attach anymore — credentials: 'include' sends
// the httpOnly access-token cookie automatically.

export async function fetchWithAuth(url: string, init: RequestInit): Promise<Response> {
  const withCreds = (extra?: RequestInit): RequestInit => ({
    ...init,
    ...extra,
    credentials: 'include',
  })

  const res = await fetch(url, withCreds())

  if (res.status === 401) {
    try {
      await attemptRefresh()
    } catch {
      throw new Error('Session expired')
    }
    return fetch(url, withCreds())
  }

  return res
}

// ── apiRequest ────────────────────────────────────────────────────────────────

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const start = performance.now()
  let status: number | 'network_error' = 'network_error'

  try {
    const result = await apiRequestInner<T>(path, options, (s) => { status = s })
    return result
  } finally {
    const durationMs = Math.round(performance.now() - start)
    if (durationMs >= SLOW_REQUEST_THRESHOLD_MS) {
      analytics.capture('slow_api_request', {
        endpoint: path, method: options.method ?? 'GET', status, duration_ms: durationMs,
      })
    }
  }
}

async function apiRequestInner<T>(
  path: string,
  options: RequestOptions,
  reportStatus: (status: number) => void,
): Promise<T> {
  const makeRequest = () =>
    fetch(`${API_BASE}${path}`, {
      method: options.method ?? 'GET',
      credentials: 'include', // sends the httpOnly access-token cookie
      headers: {
        'Content-Type': 'application/json',
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
    })

  const res = await makeRequest()
  reportStatus(res.status)

  // On 401: attempt one silent refresh, then retry the original request
  if (res.status === 401) {
    try {
      await attemptRefresh()
    } catch {
      // silentRefresh already cleared session and redirected
      throw new Error('Session expired')
    }

    const retry = await makeRequest()
    reportStatus(retry.status)

    if (retry.status === 401) {
      // Refresh succeeded but retry still 401s — genuine auth failure
      auth.markLoggedOut()
      window.location.href = '/login'
      throw new Error('Session expired')
    }

    if (!retry.ok) {
      const error = await retry.json().catch(() => ({ title: 'Request failed' }))
      const message = error.detail ?? error.title ?? 'Request failed'
      analytics.captureException(new Error(message), {
        endpoint: path, method: options.method ?? 'GET', status: retry.status, afterRefresh: true,
      })
      throw new Error(message)
    }

    if (retry.status === 204) return undefined as T
    return retry.json()
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ title: 'Request failed' }))
    const message = error.detail ?? error.title ?? 'Request failed'
    analytics.captureException(new Error(message), {
      endpoint: path, method: options.method ?? 'GET', status: res.status,
    })
    throw new Error(message)
  }

  if (res.status === 204) return undefined as T
  return res.json()
}

export const api = {
  get: <T>(path: string) => apiRequest<T>(path),
  post: <T>(path: string, body: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'DELETE', body }),
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type AiSuggestions = {
  title: string | null
  brand: string | null
  description: string | null
  condition: string | null
  activityLabels: string[]
  colorLabels: string[]
  roleLabels: string[]
  sceneLabels: string[]
  confidence: 'low' | 'medium' | 'high'
}

export type GearPhoto = {
  id: string
  storageKey: string
  url: string
  isPrimary: boolean
  ordinal: number
  altText?: string | null
}

export type GearItemAttribute = {
  id: string
  attributeType: string
  valueId: string
  priority: number
  value?: { displayLabel: string; value: string; hexValue?: string | null }
}

export type GearItem = {
  id: string
  title: string
  brand?: string | null
  description?: string | null
  notes?: string | null
  isFavorite: boolean
  productId?: string | null
  conditionId?: string | null
  statusId?: string | null
  visibilityId?: string | null
  visibility?: string | null
  exchangeAvailabilityId?: string | null
  containerId?: string | null
  sizeCategoryId?: string | null
  size?: string | null
  acquisitionSource?: string | null
  acquisitionPrice?: string | null
  acquisitionDate?: string | null
  tradeSellValue?: string | null
  createdAt: string
  updatedAt?: string
  deletedAt?: string | null
  attributes: GearItemAttribute[]
  photos: GearPhoto[]
}

export type KitCollection = {
  id: string
  name: string
  description?: string | null
  visibilityId?: string | null
  collectionTypeId?: string | null
  isFavorite: boolean
  visibility?: string | null
  itemCount: number
  isShared?: boolean
  createdAt: string
}

export type KitCollectionDeleteInfo = {
  requiresConfirmation: true
  isShared: boolean
  visibilityId: string | null
  bagName: string
}

export type Container = {
  id: string
  name: string
  description?: string | null
  barcode?: string | null
  sortOrder: number
  parentId?: string | null
  containerTypeId: string
  isDefault: boolean
  isSystem: boolean
  createdAt: string
  updatedAt: string
  typeName?: string | null
  typeLabel?: string | null
  typeFormat?: string | null
  isTopLevel?: boolean | null
}

export type GearItemNode = {
  id: string
  title: string
  brand: string | null
  containerId: string | null
}

export type ContainerNode = Container & {
  bins: ContainerNode[]
  gearItems: GearItemNode[]
}

export type FieldOption = {
  id: string
  value: string
  displayLabel: string
  attributeName: string
  hexValue?: string | null
  sortOrder: number
}

// ── Gear Items API ────────────────────────────────────────────────────────────

export const gearItemsApi = {
  list: () => api.get<{ data: GearItem[] }>('/user/gear-items'),
  listDeleted: () => api.get<{ data: GearItem[] }>('/user/gear-items?deleted=true'),
  get: (id: string) => api.get<{ data: GearItem }>(`/user/gear-items/${id}`),
  create: (body: object) => api.post<{ data: GearItem }>('/user/gear-items', body),
  update: (id: string, body: object) => api.patch<{ data: GearItem }>(`/user/gear-items/${id}`, body),
  delete: (id: string) => api.delete<{ data: { id: string; deleted: boolean } }>(`/user/gear-items/${id}`),
  toggleFavorite: (id: string) => api.post<{ data: { id: string; isFavorite: boolean } }>(`/user/gear-items/${id}/favorite`, {}),
  restore: (id: string) => api.post<{ data: { id: string; restored: boolean } }>(`/user/gear-items/${id}/restore`, {}),
  permanentDelete: (id: string) => api.delete<{ data: { id: string; permanentlyDeleted: boolean } }>(`/user/gear-items/${id}/permanent`),

  uploadPhoto: (itemId: string, formData: FormData) =>
    fetchWithAuth(`/api/user/gear-items/${itemId}/photos`, {
      method: 'POST',
      body: formData,
    }).then(r => r.json()),

  replacePhoto: (itemId: string, photoId: string, formData: FormData) =>
    fetchWithAuth(`/api/user/gear-items/${itemId}/photos/${photoId}`, {
      method: 'PUT',
      body: formData,
    }).then(r => r.json()),

  updatePhoto: (itemId: string, photoId: string, body: { isPrimary?: boolean; ordinal?: number; altText?: string }) =>
    api.patch<{ data: GearPhoto }>(`/user/gear-items/${itemId}/photos/${photoId}`, body),

  deletePhoto: (itemId: string, photoId: string) =>
    api.delete<{ data: { id: string; deleted: boolean } }>(`/user/gear-items/${itemId}/photos/${photoId}`),

  identify: (files: File[]) => {
    const fd = new FormData()
    for (const file of files) fd.append('files', file)
    return fetchWithAuth('/api/user/gear-items/identify', {
      method: 'POST',
      body: fd,
    }).then(async (r) => {
      const json = await r.json()
      if (!r.ok) {
        const message = json.detail ?? json.title ?? 'AI identification failed'
        analytics.captureException(new Error(message), { endpoint: '/user/gear-items/identify', method: 'POST', status: r.status })
        throw new Error(message)
      }
      return json as { data: AiSuggestions }
    })
  },
}

// ── Kit Collections API ───────────────────────────────────────────────────────

export const kitCollectionsApi = {
  list: () => api.get<{ data: KitCollection[] }>('/user/kit-collections'),
  get: (id: string) => api.get<{ data: KitCollection }>(`/user/kit-collections/${id}`),
  create: (body: object) => api.post<{ data: KitCollection }>('/user/kit-collections', body),
  update: (id: string, body: object) => api.patch<{ data: KitCollection }>(`/user/kit-collections/${id}`, body),
  toggleFavorite: (id: string) => api.post<{ data: { id: string; isFavorite: boolean } }>(`/user/kit-collections/${id}/favorite`, {}),
  deleteInfo: (id: string) => api.delete<{ data: KitCollectionDeleteInfo }>(`/user/kit-collections/${id}`, {}),
  deleteConfirmed: (id: string) => api.delete<{ data: { id: string; deleted: boolean } }>(`/user/kit-collections/${id}`, { confirmed: true }),
  addItem: (bagId: string, gearItemId: string) => api.post(`/user/kit-collections/${bagId}/items`, { gearItemId }),
  removeItem: (bagId: string, itemId: string) => api.delete(`/user/kit-collections/${bagId}/items/${itemId}`),
}

// ── Containers API ────────────────────────────────────────────────────────────

export const containersApi = {
  list: (params?: { parentId?: string; type?: string }) => {
    const qs = new URLSearchParams()
    if (params?.parentId) qs.set('parent_id', params.parentId)
    if (params?.type) qs.set('type', params.type)
    const query = qs.toString()
    return api.get<{ data: Container[] }>(`/user/containers${query ? `?${query}` : ''}`)
  },
  get: (id: string) => api.get<{ data: Container }>(`/user/containers/${id}`),
  contents: (id: string) => api.get<{ data: ContainerNode }>(`/user/containers/${id}/contents`),
  create: (body: object) => api.post<{ data: Container }>('/user/containers', body),
  update: (id: string, body: object) => api.patch<{ data: Container }>(`/user/containers/${id}`, body),
  move: (id: string, newParentId: string) => api.post<{ data: Container }>(`/user/containers/${id}/move`, { newParentId }),
  delete: (id: string) => api.delete<{ data: { id: string; deleted: boolean } }>(`/user/containers/${id}`),
}

// ── Field Options API ─────────────────────────────────────────────────────────

export const fieldOptionsApi = {
  quickAdd: (attribute: string, body: Record<string, string>) =>
    api.post<{ data: FieldOption }>(`/user/field-options/${attribute}`, body),
  getByAttribute: (attribute: string) =>
    api.get<{ data: FieldOption[] }>(`/user/field-options/${attribute}`),
}
